import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { FileText, CheckCircle, XCircle, AlertCircle, Download, Loader2, AlertTriangle, BookOpen } from "lucide-react"
import { type DatasourceBill, type DatasourceBillVersion, useGetDatasourceFileContent } from '@/api/datasource-bills-api'
import { useState } from 'react'
import * as React from 'react'
import { useToast } from '@/hooks/use-toast'
import { BillFullTextDialog } from './BillFullTextDialog'

// Types for amendment data
interface Amendment {
  type: 'strikeout' | 'underline'
  text: string
  section?: string
}

interface PageResult {
  page_number: number
  amendments: Amendment[]
  sections_processed?: number
}

interface AmendmentData {
  bill_number: string
  session_code: string
  version_number: number
  processed_at: string
  total_pages: number
  processed_pages: number
  pages_analyzed: number
  total_cost: number
  page_results: PageResult[]
  summary: {
    total_amendments: number
    strikeout_count: number
    underline_count: number
    pages_with_amendments: number
  }
}

interface BillVersionsDialogProps {
  bill: DatasourceBill | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BillVersionsDialog({ bill, open, onOpenChange }: BillVersionsDialogProps) {
  const [fullAmendmentData, setFullAmendmentData] = useState<Record<number, AmendmentData>>({})
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<string>("overview")
  const [fullTextDialogOpen, setFullTextDialogOpen] = useState(false)
  const fileContentMutation = useGetDatasourceFileContent()
  const { toast } = useToast()

  // Initialize selected version to the first version (don't auto-select amendments version)
  const initializeSelectedVersion = React.useCallback(() => {
    if (bill && selectedVersion === null && bill.versions.length > 0) {
      // Just select the first version, don't bias towards amendments
      setSelectedVersion(bill.versions[0].version)
    }
  }, [bill, selectedVersion])

  // Reset state when bill changes
  React.useEffect(() => {
    if (bill) {
      setSelectedVersion(null)
      setFullAmendmentData({})
      setActiveTab("overview")
    }
  }, [bill?.bill_number]) // Use bill_number as dependency to detect bill changes

  // Call initialization when dialog opens or bill changes
  React.useEffect(() => {
    if (open && bill) {
      initializeSelectedVersion()
    }
  }, [open, bill, initializeSelectedVersion])

  const handleViewAmendments = React.useCallback(async (versionNumber: number, switchToTab: boolean = false) => {
    if (!bill) return
    
    const version = bill.versions.find(v => v.version === versionNumber)
    if (!version || !version.amendments_analysis?.s3_key || !version.amendments_detected) {
      toast({
        title: "No Amendments Available", 
        description: "Amendment data is not available for this version.",
        variant: "destructive"
      })
      return
    }

    // Prevent duplicate fetches for the same version
    if (fileContentMutation.isPending) {
      return
    }

    try {
      const content = await fileContentMutation.mutateAsync(version.amendments_analysis.s3_key)
      const amendmentData: AmendmentData = JSON.parse(content)
      
      setFullAmendmentData(prev => ({
        ...prev,
        [version.version]: amendmentData
      }))
      
      // Only switch to amendments tab if explicitly requested
      if (switchToTab) {
        setActiveTab("amendments")
      }
    } catch (error) {
      console.error('Failed to fetch amendment data:', error)
      toast({
        title: "Failed to Load Amendments",
        description: "Could not load amendment data.",
        variant: "destructive"
      })
    }
  }, [bill, fileContentMutation, toast])

  // Auto-load amendment data when switching to amendments tab with a selected version
  React.useEffect(() => {
    if (bill && activeTab === "amendments" && selectedVersion) {
      const selectedVersionData = bill.versions.find(v => v.version === selectedVersion)
      if (selectedVersionData?.amendments_detected && 
          selectedVersionData?.amendments_analysis?.s3_key && 
          !fullAmendmentData[selectedVersion] &&
          !fileContentMutation.isPending) {
        handleViewAmendments(selectedVersion)
      }
    }
  }, [activeTab, selectedVersion, fullAmendmentData, bill, handleViewAmendments, fileContentMutation.isPending])

  // Early return after all hooks have been called
  if (!bill) return null

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return '—'
    }
  }

  const formatCost = (cost: any): string => {
    if (!cost) return 'N/A'
    const numCost = typeof cost === 'number' ? cost : parseFloat(cost)
    return isNaN(numCost) ? String(cost) : numCost.toFixed(4)
  }

  const getVersionBadge = (version: DatasourceBillVersion) => {
    const hasText = version.text_extracted && version.text_s3_key
    const amendmentStatus = version.amendments_detected
    
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="font-mono">
          V{version.version}
        </Badge>
        
        {hasText && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Text
          </Badge>
        )}
        
        {amendmentStatus === true && (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Amendments
          </Badge>
        )}
        
        {amendmentStatus === false && (
          <Badge variant="outline" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Clean
          </Badge>
        )}
      </div>
    )
  }



  const handleVersionChange = (versionNumber: number) => {
    setSelectedVersion(versionNumber)
    // If switching to a version with amendments and we're on the amendments tab, load the data
    if (activeTab === "amendments") {
      const version = bill.versions.find(v => v.version === versionNumber)
      if (version?.amendments_detected && !fullAmendmentData[versionNumber] && !fileContentMutation.isPending) {
        handleViewAmendments(versionNumber)
      }
    }
  }

  const renderAmendmentText = (amendment: Amendment) => {
    if (amendment.type === 'strikeout') {
      return (
        <span className="line-through text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1 rounded">
          {amendment.text}
        </span>
      )
    } else {
      return (
        <span className="underline text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1 rounded">
          {amendment.text}
        </span>
      )
    }
  }

  const renderAmendmentsTab = () => {
    if (!selectedVersion) {
      return (
        <div className="text-center py-8">
          <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Version Selected</h3>
          <p className="text-muted-foreground">
            Select a version from the header to view amendment information.
          </p>
        </div>
      )
    }

    const selectedVersionData = bill.versions.find(v => v.version === selectedVersion)
    if (!selectedVersionData?.amendments_detected) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Amendments in Version {selectedVersion}</h3>
          <p className="text-muted-foreground">
            This version does not contain any amendments. Select a version with amendments from the header.
          </p>
        </div>
      )
    }

    if (!fullAmendmentData[selectedVersion]) {
      return (
        <div className="text-center py-8">
          <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
          <h3 className="text-lg font-medium mb-2">Loading Amendment Data</h3>
          <p className="text-muted-foreground">
            Fetching detailed amendment information for Version {selectedVersion}...
          </p>
        </div>
      )
    }

    const amendmentData = fullAmendmentData[selectedVersion]

    return (
      <div className="space-y-4">
        {/* Amendment Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Amendment Summary - Version {selectedVersion}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{amendmentData.summary.total_amendments}</div>
                <div className="text-sm text-muted-foreground">Total Amendments</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{amendmentData.summary.strikeout_count}</div>
                <div className="text-sm text-muted-foreground">Strikeouts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{amendmentData.summary.underline_count}</div>
                <div className="text-sm text-muted-foreground">Underlines</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{amendmentData.summary.pages_with_amendments}</div>
                <div className="text-sm text-muted-foreground">Pages Affected</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Page-by-Page Amendments */}
        <div className="space-y-4">
          {amendmentData.page_results.map((pageResult) => (
            <Card key={pageResult.page_number}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Page {pageResult.page_number}</span>
                  <Badge variant="outline">
                    {pageResult.amendments.length} {pageResult.amendments.length === 1 ? 'amendment' : 'amendments'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pageResult.amendments.length > 0 ? (
                  <div className="space-y-3">
                    {pageResult.amendments.map((amendment, idx) => (
                      <div key={idx} className="border-l-4 border-l-slate-200 dark:border-l-slate-700 pl-4 py-2">
                        <div className="flex items-start gap-3">
                          <Badge 
                            variant={amendment.type === 'strikeout' ? 'destructive' : 'default'}
                            className="shrink-0 mt-0.5"
                          >
                            {amendment.type}
                          </Badge>
                          <div className="flex-1">
                            <div className="font-mono text-sm bg-white dark:bg-slate-900 border rounded p-2">
                              {renderAmendmentText(amendment)}
                            </div>
                            {amendment.section && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Section: {amendment.section}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    No amendments found on this page
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Processing Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Processing Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium text-muted-foreground mb-2">Processing Details</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Processed At:</span>
                    <span>{new Date(amendmentData.processed_at).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Pages:</span>
                    <span>{amendmentData.total_pages}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pages Processed:</span>
                    <span>{amendmentData.processed_pages}</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground mb-2">Cost Information</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Processing Cost:</span>
                    <span>${amendmentData.total_cost.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pages Analyzed:</span>
                    <span>{amendmentData.pages_analyzed}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }


  const handleViewFullText = (version: DatasourceBillVersion) => {
    if (!version.text_s3_key) {
      toast({
        title: "No Text Available",
        description: "Full text is not available for this version.",
        variant: "destructive"
      })
      return
    }
    setFullTextDialogOpen(true)
  }

  const hasAmendments = bill.versions.some(v => v.amendments_detected === true)
  const processedVersions = bill.versions.filter(v => v.amendments_detected !== undefined)
  const versionsWithText = bill.versions.filter(v => v.text_extracted && v.text_s3_key)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Versions & Amendments - {bill.bill_number}
            </DialogTitle>
            
            {/* Version Selector */}
            <div className="flex items-center gap-2 pt-2">
              <span className="text-sm font-medium text-muted-foreground">Version:</span>
              <div className="flex items-center gap-1 flex-wrap">
                {bill.versions.map((version) => (
                  <Button
                    key={version.version}
                    variant={selectedVersion === version.version ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleVersionChange(version.version)}
                    className="flex items-center gap-1 h-7"
                  >
                    <span className="font-mono">V{version.version}</span>
                    {version.amendments_detected === true && (
                      <AlertTriangle className="h-3 w-3 text-red-500" />
                    )}
                    {version.amendments_detected === false && (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    )}
                  </Button>
                ))}
              </div>
            </div>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="amendments" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Amendments
                {bill.versions.filter(v => v.amendments_detected === true).length > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                    {bill.versions.filter(v => v.amendments_detected === true).length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="flex-1">
              <ScrollArea className="h-full max-h-[70vh]">
                <div className="space-y-4 pr-4">
                  {/* Bill Summary */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Bill Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{bill.versions.length}</div>
                          <div className="text-sm text-muted-foreground">Total Versions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{versionsWithText.length}</div>
                          <div className="text-sm text-muted-foreground">With Text</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{processedVersions.length}</div>
                          <div className="text-sm text-muted-foreground">Processed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">
                            {bill.versions.filter(v => v.amendments_detected === true).length}
                          </div>
                          <div className="text-sm text-muted-foreground">With Amendments</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-600">
                            {bill.versions.filter(v => v.amendments_detected === false).length}
                          </div>
                          <div className="text-sm text-muted-foreground">Clean Versions</div>
                        </div>
                      </div>
                      
                      {hasAmendments && (
                        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                            <AlertCircle className="h-4 w-4" />
                            <span className="font-medium">Amendments detected in this bill</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Selected Version Details */}
                  {selectedVersion && (() => {
                    const version = bill.versions.find(v => v.version === selectedVersion)
                    if (!version) return null

                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-3">
                            Version {selectedVersion} Details
                            {getVersionBadge(version)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Version Info Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <div className="font-medium text-muted-foreground mb-3">Basic Information</div>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <span>Created At:</span>
                                  <span className="font-mono text-xs">
                                    {formatDate(version.created_at)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span>MD5 Hash:</span>
                                  <span className="font-mono text-xs truncate max-w-[150px]" title={version.md5_hash}>
                                    {version.md5_hash}
                                  </span>
                                </div>
                                {version.textract_job_id && (
                                  <div className="flex items-center justify-between">
                                    <span>Textract Job ID:</span>
                                    <span className="font-mono text-xs truncate max-w-[150px]" title={version.textract_job_id}>
                                      {version.textract_job_id}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div>
                              <div className="font-medium text-muted-foreground mb-3">Processing Status</div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">Text Extracted</span>
                                  {version.text_extracted ? (
                                    <Badge variant="default" className="flex items-center gap-1">
                                      <CheckCircle className="h-3 w-3" />
                                      Yes
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="flex items-center gap-1">
                                      <XCircle className="h-3 w-3" />
                                      No
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">Amendments Processed</span>
                                  {version.amendments_detected !== undefined ? (
                                    <Badge variant="default" className="flex items-center gap-1">
                                      <CheckCircle className="h-3 w-3" />
                                      Yes
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="flex items-center gap-1">
                                      <XCircle className="h-3 w-3" />
                                      No
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">Amendment Status</span>
                                  {version.amendments_detected === true ? (
                                    <Badge variant="destructive" className="flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Has Amendments
                                    </Badge>
                                  ) : version.amendments_detected === false ? (
                                    <Badge variant="outline" className="flex items-center gap-1">
                                      <CheckCircle className="h-3 w-3" />
                                      Clean
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary">
                                      Not Processed
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* File Information */}
                          <div>
                            <div className="font-medium text-muted-foreground mb-3">File Information</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              {version.text_s3_key && (
                                <div>
                                  <span className="font-medium">Text S3 Key:</span>
                                  <div className="font-mono text-xs text-muted-foreground mt-1 break-all">
                                    {version.text_s3_key}
                                  </div>
                                </div>
                              )}
                              {version.pdf_s3_key && (
                                <div>
                                  <span className="font-medium">PDF S3 Key:</span>
                                  <div className="font-mono text-xs text-muted-foreground mt-1 break-all">
                                    {version.pdf_s3_key}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Amendment Analysis Details */}
                          {version.amendments_analysis && (
                            <div>
                              <div className="font-medium text-muted-foreground mb-3">Amendment Analysis</div>
                              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                                {version.amendments_analysis?.summary ? (
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-blue-600">
                                        {version.amendments_analysis.summary.total_amendments || 0}
                                      </div>
                                      <div className="text-xs text-muted-foreground">Total Amendments</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-red-600">
                                        {version.amendments_analysis.summary.strikeout_count || 0}
                                      </div>
                                      <div className="text-xs text-muted-foreground">Strikeouts</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-green-600">
                                        {version.amendments_analysis.summary.underline_count || 0}
                                      </div>
                                      <div className="text-xs text-muted-foreground">Underlines</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-xl font-bold text-purple-600">
                                        {version.amendments_analysis.summary.pages_with_amendments || 0}
                                      </div>
                                      <div className="text-xs text-muted-foreground">Pages Affected</div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm text-muted-foreground">Analysis data available</div>
                                )}
                                
                                {(version.amendments_analysis.processed_pages || version.amendments_analysis.cost) && (
                                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      {version.amendments_analysis.processed_pages && (
                                        <div>
                                          <span className="font-medium">Pages Processed:</span>
                                          <span className="ml-2">{version.amendments_analysis.processed_pages}</span>
                                        </div>
                                      )}
                                      {version.amendments_analysis.cost && (
                                        <div>
                                          <span className="font-medium">Processing Cost:</span>
                                          <span className="ml-2">${formatCost(version.amendments_analysis.cost)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="pt-4 border-t">
                            <div className="flex items-center gap-2 flex-wrap">
                              {version.text_s3_key && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="flex items-center gap-1"
                                  onClick={() => handleViewFullText(version)}
                                >
                                  <FileText className="h-3 w-3" />
                                  View Full Text
                                </Button>
                              )}
                              {version.pdf_s3_key && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="flex items-center gap-1"
                                  onClick={() => window.open(version.pdf_s3_key, '_blank', 'noopener,noreferrer')}
                                >
                                  <Download className="h-3 w-3" />
                                  Download PDF
                                </Button>
                              )}
                              {version.amendments_detected && version.amendments_analysis?.s3_key && (
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="flex items-center gap-1"
                                  onClick={() => {
                                    handleViewAmendments(version.version, true) // Pass true to switch to tab
                                  }}
                                  disabled={fileContentMutation.isPending}
                                >
                                  {fileContentMutation.isPending && selectedVersion === version.version ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <BookOpen className="h-3 w-3" />
                                  )}
                                  View Amendments
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })()}

                  {/* No Version Selected */}
                  {!selectedVersion && (
                    <Card>
                      <CardContent className="text-center py-8">
                        <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">No Version Selected</h3>
                        <p className="text-muted-foreground">
                          Select a version from the header to view detailed information.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="amendments" className="flex-1">
              <ScrollArea className="h-full max-h-[70vh]">
                <div className="pr-4">
                  {renderAmendmentsTab()}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Full Text Dialog */}
      <BillFullTextDialog
        bill={bill}
        open={fullTextDialogOpen}
        onOpenChange={setFullTextDialogOpen}
      />
    </>
  )
}